import { Listbox, Combobox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  fetch as fetchUsers,
  fetchById as fetchUserById,
} from "services/user-select.service";
import { useState, useCallback, useEffect, useMemo } from "react";

interface Option {
  value: string;
  label: string;
  /** Lower-cased, accent-free text the search box matches against. */
  haystack: string;
}

interface UserRow {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  externalCode?: string;
}

interface UserFilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  labelHidden?: boolean;
  placeholder?: string;
}

// "Šarūnas" must be found by typing "sarunas" — people rarely type the
// Lithuanian letters into a search box.
const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const toOption = (u: UserRow): Option => {
  const label = u.name || u.email || u.id;
  return {
    value: u.id,
    label,
    haystack: fold(
      [u.name, u.email, u.phone, u.externalCode].filter(Boolean).join(" ")
    ),
  };
};

const UserFilterSelect = ({
  value,
  onChange,
  label,
  labelHidden,
  placeholder,
}: UserFilterSelectProps) => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [allOptions, setAllOptions] = useState<Option[]>([]);
  // Starts true so the saved value waits for the list instead of racing it
  // with a lookup of its own.
  const [loading, setLoading] = useState(Boolean(companyId));

  // The whole company list is loaded up front, so opening the field already
  // shows everybody and typing only narrows it down. The endpoint ignores
  // search text shorter than four characters anyway, so asking it per
  // keystroke never narrowed anything for short input.
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchUsers(companyId, { textSearch: "" })
      .then((data: { usersCollection?: UserRow[]; users?: UserRow[] }) => {
        if (cancelled) return;
        const collection = data?.usersCollection ?? data?.users ?? [];
        setAllOptions(
          collection
            .map(toOption)
            .sort((a, b) => a.label.localeCompare(b.label, "lt"))
        );
      })
      .catch(() => {
        if (!cancelled) setAllOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!value) {
      setSelectedOptions([]);
      return undefined;
    }
    const known = allOptions.find((option) => option.value === value);
    if (known) {
      setSelectedOptions([known]);
      return undefined;
    }
    // Not in the list (still loading, or a disabled user saved earlier) —
    // resolve the name on its own so the tag never shows a bare id.
    if (!companyId || loading) return undefined;
    let cancelled = false;
    fetchUserById(value, companyId)
      .then(({ userEntity }: { userEntity: UserRow }) => {
        if (!cancelled) setSelectedOptions([toOption(userEntity)]);
      })
      .catch(() => {
        if (!cancelled) setSelectedOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [value, companyId, allOptions, loading]);

  const options = useMemo(() => {
    const needle = fold(inputValue.trim());
    if (!needle) return allOptions;
    return allOptions.filter((option) => option.haystack.includes(needle));
  }, [allOptions, inputValue]);

  const handleSelection = useCallback(
    (selected: string) => {
      const findSelectedOption = allOptions.find(
        (option) => option.value === selected
      );
      if (findSelectedOption) {
        setSelectedOptions([findSelectedOption]);
        setInputValue("");
        onChange(selected);
      }
    },
    [allOptions, onChange]
  );

  const handleTagRemove = useCallback(() => {
    setSelectedOptions([]);
    onChange("");
  }, [onChange]);

  const optionsMarkup = options.map((option) => (
    <Listbox.Option
      key={option.value}
      value={option.value}
      selected={selectedOptions.some((o) => o.value === option.value)}
      accessibilityLabel={option.label}
    >
      {option.label}
    </Listbox.Option>
  ));

  const verticalContentMarkup =
    selectedOptions.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        <Tag onRemove={handleTagRemove}>{selectedOptions[0].label}</Tag>
      </LegacyStack>
    ) : null;

  return (
    <div>
      <Combobox
        allowMultiple={false}
        activator={
          <Combobox.TextField
            prefix={<Icon source={SearchIcon as any} />}
            onChange={setInputValue}
            label={label ?? t("user")}
            labelHidden={labelHidden}
            value={inputValue}
            placeholder={
              placeholder ??
              (t("user_search") !== "user_search"
                ? t("user_search")
                : "Search user...")
            }
            autoComplete="off"
            verticalContent={verticalContentMarkup}
          />
        }
      >
        <Listbox onSelect={handleSelection}>
          {loading ? (
            <Listbox.Loading accessibilityLabel="Loading" />
          ) : (
            <>{optionsMarkup}</>
          )}
        </Listbox>
      </Combobox>
    </div>
  );
};

export default UserFilterSelect;
